import ky from "ky";
import type { z } from "zod";
import {
  RequestKitInitSchema,
  type RequestKitSchema,
  ErrorResponseSchema,
  type ErrorResponse,
} from "./schema";

export class RequestKitError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: Record<string, unknown>,
    public status?: number
  ) {
    super(message);
    this.name = "RequestKitError";
  }
}

export class RequestKit {
  private $fetch: ReturnType<typeof ky.create>;

  constructor(protected options: z.infer<typeof RequestKitInitSchema>) {
    const data = RequestKitInitSchema.parse(options);

    // Create a ky instance with default options
    this.$fetch = ky.create({
      prefixUrl: data.baseURL ?? "https://requestkit.dev/api/v1",
      headers: {
        "x-request-kit-key": data.apiKey,
      },
      retry: {
        limit: data.retries ?? 3,
        methods: ["get", "put", "head", "delete", "options", "trace"],
        statusCodes: [408, 413, 429, 500, 502, 503, 504],
        backoffLimit: 1000,
        delay: (attempt) => attempt * 1000, // Linear backoff
      },
      hooks: {
        beforeRequest: [
          (request) => {
            if (data.logging?.enabled) {
              console.log(`[RequestKit] ${request.method} ${request.url}`);
            }
            return request;
          },
        ],
        afterResponse: [
          (request, options, response) => {
            if (data.logging?.enabled) {
              console.log(
                `[RequestKit] ${response.status} ${request.method} ${request.url}`
              );
            }
            return response;
          },
        ],
      },
    });
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      if (errorData && ErrorResponseSchema.safeParse(errorData).success) {
        const parsedError = errorData as ErrorResponse;
        throw new RequestKitError(
          parsedError.message,
          parsedError.code,
          parsedError.details,
          response.status
        );
      }
      throw new RequestKitError(
        `HTTP error! status: ${response.status}`,
        undefined,
        undefined,
        response.status
      );
    }
    return response.json();
  }

  async getRequests(query?: RequestKitSchema["@get/requests"]["query"]) {
    const formattedQuery = query
      ? (Object.fromEntries(
          Object.entries({
            ...query,
            "orderBy[]": query.orderBy?.join(","),
            orderBy: undefined, // Remove the original orderBy to avoid duplicate params
          }).filter(([_, value]) => value !== undefined)
        ) as Record<string, string | number | boolean>)
      : undefined;

    try {
      const response = await this.$fetch("requests", {
        searchParams: formattedQuery,
      });
      return this.handleResponse<RequestKitSchema["@get/requests"]["output"]>(
        response
      );
    } catch (error) {
      if (error instanceof RequestKitError) {
        throw error;
      }
      throw new RequestKitError(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }

  async createRequest(input: RequestKitSchema["@post/requests"]["input"]) {
    const formData = new FormData();
    formData.append("title", input.title);
    formData.append("description", input.description);
    if (input.status) {
      formData.append("status", input.status);
    }
    if (input.requester) {
      formData.append("requester.identifier", input.requester.identifier);
      if (input.requester.name) {
        formData.append("requester.name", input.requester.name);
      }
      if (input.requester.email) {
        formData.append("requester.email", input.requester.email);
      }
    }
    if (input.files) {
      // Ensure we're appending each file individually
      const files = Array.isArray(input.files) ? input.files : [input.files];
      for (const file of files) {
        formData.append("files[]", file);
      }
    }

    try {
      const response = await this.$fetch("requests", {
        method: "POST",
        body: formData,
      });
      return this.handleResponse<RequestKitSchema["@post/requests"]["output"]>(
        response
      );
    } catch (error) {
      if (error instanceof RequestKitError) {
        throw error;
      }
      throw new RequestKitError(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }

  async upvoteRequest(
    id: RequestKitSchema["@post/requests/:id/upvote"]["params"]["id"],
    input: RequestKitSchema["@post/requests/:id/upvote"]["input"]
  ): Promise<RequestKitSchema["@post/requests/:id/upvote"]["output"]> {
    try {
      const response = await this.$fetch(`requests/${id}/upvote`, {
        method: "POST",
        json: input,
      });
      return this.handleResponse<
        RequestKitSchema["@post/requests/:id/upvote"]["output"]
      >(response);
    } catch (error) {
      if (error instanceof RequestKitError) {
        throw error;
      }
      throw new RequestKitError(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }

  async removeUpvoteFromRequest(
    id: RequestKitSchema["@delete/requests/:id/upvote"]["params"]["id"],
    query: RequestKitSchema["@delete/requests/:id/upvote"]["query"]
  ) {
    try {
      const response = await this.$fetch(`requests/${id}/upvote`, {
        method: "DELETE",
        searchParams: query,
      });
      return this.handleResponse<
        RequestKitSchema["@delete/requests/:id/upvote"]["output"]
      >(response);
    } catch (error) {
      if (error instanceof RequestKitError) {
        throw error;
      }
      throw new RequestKitError(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }

  async getRequest(
    id: RequestKitSchema["@get/requests/:id"]["params"]["id"],
    query: RequestKitSchema["@get/requests/:id"]["query"]
  ) {
    try {
      const response = await this.$fetch(`requests/${id}`, {
        searchParams: query,
      });
      return this.handleResponse<
        RequestKitSchema["@get/requests/:id"]["output"]
      >(response);
    } catch (error) {
      if (error instanceof RequestKitError) {
        throw error;
      }
      throw new RequestKitError(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }

  async getComments(
    requestId: RequestKitSchema["@get/requests/:id/comments"]["params"]["id"],
    query?: RequestKitSchema["@get/requests/:id/comments"]["query"]
  ) {
    try {
      const response = await this.$fetch(`requests/${requestId}/comments`, {
        searchParams: query,
      });
      return this.handleResponse<
        RequestKitSchema["@get/requests/:id/comments"]["output"]
      >(response);
    } catch (error) {
      if (error instanceof RequestKitError) {
        throw error;
      }
      throw new RequestKitError(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }

  async createComment(
    requestId: RequestKitSchema["@post/requests/:id/comments"]["params"]["id"],
    input: RequestKitSchema["@post/requests/:id/comments"]["input"]
  ) {
    const formData = new FormData();
    formData.append("content", input.content);
    formData.append("commentor.identifier", input.commentor.identifier);
    if (input.commentor.name) {
      formData.append("commentor.name", input.commentor.name);
    }
    if (input.commentor.email) {
      formData.append("commentor.email", input.commentor.email);
    }
    if (input.files) {
      // Ensure files are always appended as an array
      const files = Array.isArray(input.files) ? input.files : [input.files];
      files.forEach((file: Blob) => {
        formData.append("files", file);
      });
    }

    try {
      const response = await this.$fetch(`requests/${requestId}/comments`, {
        method: "POST",
        body: formData,
      });
      return this.handleResponse<
        RequestKitSchema["@post/requests/:id/comments"]["output"]
      >(response);
    } catch (error) {
      if (error instanceof RequestKitError) {
        throw error;
      }
      throw new RequestKitError(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }

  async deleteComment(
    requestId: RequestKitSchema["@delete/requests/:id/comments/:commentId"]["params"]["id"],
    commentId: RequestKitSchema["@delete/requests/:id/comments/:commentId"]["params"]["commentId"],
    body: RequestKitSchema["@delete/requests/:id/comments/:commentId"]["body"]
  ) {
    try {
      const response = await this.$fetch(
        `requests/${requestId}/comments/${commentId}`,
        {
          method: "DELETE",
          json: body,
        }
      );
      return this.handleResponse<
        RequestKitSchema["@delete/requests/:id/comments/:commentId"]["output"]
      >(response);
    } catch (error) {
      if (error instanceof RequestKitError) {
        throw error;
      }
      throw new RequestKitError(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }
}